import { Button } from '@naf/button';
import { Text, TextVariant } from '@naf/text';
import { themeLight, spacing, breakpoints } from '@naf/theme';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CloudinaryImageType } from '../../../../../types/blockTypes';
import { CountdownBlockType } from '../../../../../types/countdownBlockType';
import { LinkType } from '../../../../../types/LinkType';
import { useDocumentUrlWithNode } from '../../../hooks/useDocumentUrl';
import useSelector from '../../../redux/typedHooks';
import { cloudinaryImage } from '../../../utils/imageUrl';
import Timer from './CountdownBlock/Timer';

type CountdownBlockProps = { block: CountdownBlockType };

function useImgUrl(img?: CloudinaryImageType) {
  const app = useSelector((state) => state.application);
  const cldImg = img?.publicId ? cloudinaryImage(img.publicId, app) : null;
  const fallbackImg = img?.srcSet?.jpgLarge;
  return cldImg || fallbackImg || '';
}

const CountdownBlock = ({ block }: CountdownBlockProps) => {
  const { backgroundImage: bgImg, logoImage: logo, countdownTo, tagline, linkBlock } = block;

  const backgroundImage = useImgUrl(bgImg);
  const logoImage = useImgUrl(logo);

  return (
    <CardBackground $backgroundSrc={backgroundImage}>
      <TextContainer>
        <LogoImage alt={logo?.altText || 'Logo'} src={logoImage} />
        <Text variant={TextVariant.Header1} tag="h2">
          {tagline}
        </Text>
        <Timer targetTime={countdownTo} />
        {linkBlock?.externalTarget?.href || linkBlock?.internalReference?.slug ? <LinkField link={linkBlock} /> : null}
      </TextContainer>
    </CardBackground>
  );
};

const CardBackground = styled.div<{ $backgroundSrc: string }>`
  background-image: url('${({ $backgroundSrc }) => $backgroundSrc}');
  background-position: center;
  background-size: cover;

  color: ${themeLight.background.default};
`;

const TextContainer = styled.div`
  max-width: ${breakpoints.s};
  padding: ${spacing.space40} ${spacing.space24};
  margin: 0 auto;

  display: flex;
  flex-flow: column nowrap;
  align-items: center;

  & h2 {
    text-align: center;
    margin-top: 0;
    margin-bottom: ${spacing.space16};
  }

  & > a {
    margin-top: ${spacing.space24};
    box-shadow: 0 2px ${spacing.space12} rgba(0, 0, 0, 0.24);
    // For shadow to match corners of the button inside
    border-radius: 2px;
  }

  h2,
  h3,
  h6 {
    text-shadow: 0 2px ${spacing.space12} rgba(0, 0, 0, 0.24);
  }
`;

const LogoImage = styled.img`
  height: ${spacing.space24};
  margin-bottom: ${spacing.space24};
`;

const InternalLinkReferenceView = ({ link }: { link: LinkType }) => {
  const target = useDocumentUrlWithNode(link.internalReference);
  if (!target) return null;
  return (
    <UnstyledLink to={target}>
      <Button onClick={() => {}}>Gå til testen</Button>
    </UnstyledLink>
  );
};

const LinkField = ({ link }: { link: LinkType }) => {
  switch (link.targetType) {
    case 'internalReference':
      if (link.internalReference) {
        return <InternalLinkReferenceView link={link} />;
      }
      return null;
    case 'externalTarget':
      if (link.externalTarget) {
        return <Button href={link.externalTarget.href}>Gå til testen</Button>;
      }
      return null;
    default:
      return null;
  }
};

const UnstyledLink = styled(Link)`
  text-decoration: none;
`;

export { CountdownBlock, useImgUrl };
